import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Typography,
  Grid,
  Box,
  DialogContent,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'utils/makeStylesAdapter';
import { useDropzone } from 'react-dropzone';
import DescriptionIcon from '@mui/icons-material/Description';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { toast } from 'react-toastify';
import HeaderModal from 'components/HeaderModal';
import { colors } from 'utils/colorsSteps';
import ButtonConsensus from 'components/ButtonConsensus';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/HelpOutline';
import {
  PJE_FILE_MAX_SIZE_BY_TYPE,
  parseFileProcessStatus,
  parseFileProcessError,
  PJE_ALLOWED_EXTENSIONS,
} from './utils';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import inquiryService from 'services/inquiries/inquiryService';

const useStyles = makeStyles(theme => ({
  dropzone: {
    height: '100%',
    width: '100%',
    border: '1px dashed #292929',
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 10,
    cursor: 'pointer',
  },
  containerModal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  circularProgress: {
    '& > * + *': {
      marginLeft: theme.spacing(2),
    },
    zIndex: 5,
    height: '100%',
  },
}));

const TableFilesAllowed = props => {
  return (
    <div style={{ backgroundColor: 'white' }}>
      <Typography style={{ color: 'darkred' }}>{props.message}</Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Tipos de arquivos permitidos</TableCell>
            <TableCell>Tamanho máximo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(PJE_FILE_MAX_SIZE_BY_TYPE).map((type, index) => (
            <TableRow key={index}>
              <TableCell>{type}</TableCell>
              <TableCell>{PJE_FILE_MAX_SIZE_BY_TYPE[type] / (1024 * 1024)}MB</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const ModalAddFile = props => {
  const { isUrgent, setIsUrgent, showUrgent } = props;
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [filesInfo, setFilesInfo] = useState({});

  const handleChangeUrgent = async event => {
    const checked = event.target.checked;
    setIsUrgent(checked);
    inquiryService
      .updateIsUrgentService(props.inquiryInvestigatedId, { isUrgent: checked })
      .then(() => {
        toast.success('Status de urgência atualizado com sucesso!');
      })
      .catch(() => {
        toast.error('Erro ao atualizar status de urgência');
      });
  };

  const {
    open,
    onClose,
    pjeFileOptions,
    setPjeFileOptions,
    movementFiles,
    setMovementFiles,
    disableFiles,
  } = props;

  const [loading] = useState(false);

  const handleClose = () => {
    onClose();
  };

  const handleSelectFile = ({ target }) => {
    const fileIndex = parseInt(target.id);
    if (fileIndex === -1) {
      return;
    }
    const file = pjeFileOptions[fileIndex];
    if (file.disabledReprocess) {
      return;
    }
    file.sendToPJE = target.checked;
    setPjeFileOptions([...pjeFileOptions]);
  };

  const handleRemoveFile = (item, index) => {
    setMovementFiles(files => files.filter((_item, fileIndex) => fileIndex !== index));
    setPjeFileOptions(files => files.filter(_item => _item.id !== item.id));
  };

  const hasInfoError = (fileName = '') => filesInfo[fileName]?.type === 'error';

  const validateFiles = files => {
    let info = filesInfo;
    files.forEach(file => {
      const fileExtension = file.name
        .split('.')
        .pop()
        .toUpperCase();

      if (!PJE_FILE_MAX_SIZE_BY_TYPE[fileExtension]) {
        info = {
          ...info,
          [file.name]: {
            message: 'Tipo de arquivo não permitido. Será ignorado.',
            type: 'error',
          },
        };
      }
      if (PJE_FILE_MAX_SIZE_BY_TYPE[fileExtension] < file.size) {
        if (fileExtension === 'PDF') {
          info = {
            ...info,
            [file.name]: {
              message:
                'O arquivo excede o tamanho máximo permitido. Será divido em partes menores.',
              type: 'warning',
            },
          };
        } else {
          info = {
            ...info,
            [file.name]: {
              message: 'O arquivo excede o tamanho máximo permitido. Será ignorado',
              type: 'error',
            },
          };
        }
      }
      setFilesInfo(info);
    });
  };

  useEffect(() => {
    validateFiles(movementFiles);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    //accept: 'application/pdf',
    multiple: true,
    onDrop: files => validateFiles(getBase64(files)),
  });

  //Converter arquivo para Base64
  const getBase64 = files => {
    files.forEach(file => {
      const { size } = file;
      const fileExtension = file.name
        .split('.')
        .pop()
        .toUpperCase();

      if (!PJE_ALLOWED_EXTENSIONS.includes(fileExtension)) {
        toast.error(`O tipo de arquivo .${fileExtension} não é aceito pelo PJE.`);
        return;
      }

      var reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result;
        setMovementFiles(files => [
          ...files,
          {
            name: file.name,
            base64,
            ignore:
              !PJE_FILE_MAX_SIZE_BY_TYPE[fileExtension] ||
              (fileExtension !== 'PDF' && size > PJE_FILE_MAX_SIZE_BY_TYPE[fileExtension]),
            size,
          },
        ]);
      };
      reader.onerror = function(error) {
        toast.warn(error);
      };
    });
    return files;
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      onClose={handleClose}
      open={open}
      className={classes.modal}
      maxWidth="sm"
      fullWidth
    >
      <HeaderModal
        onClose={handleClose}
        backgroundColor={colors.navBar}
        colorIconTitle={colors.greySecundary}
      >
        Anexar Arquivos
      </HeaderModal>
      <DialogContent>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
          <Box mt={2}>
            <Typography variant="h5">Arquivos selecionados</Typography>
          </Box>
        </Grid>
        <Grid container spacing={2}>
          <Grid style={{ minHeight: 150 }} size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box {...getRootProps({ className: 'dropzone' })} className={classes.dropzone} mt={2}>
              <input {...getInputProps()} />
              <Typography>Arraste um arquivo, ou clique para adicionar</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box mt={2}>
              <Typography variant="h5" style={{ display: 'flex' }}>
                Arquivos selecionados
                <span style={{ marginLeft: 5 }}>
                  <Tooltip
                    arrow
                    placement="right"
                    title={<TableFilesAllowed />}
                    style={{ backgroundColor: 'white' }}
                  >
                    <HelpIcon style={{ color: 'darkgray', fontSize: '15px', marginTop: '3px' }} />
                  </Tooltip>
                </span>
              </Typography>
              {movementFiles?.length > 0 &&
                movementFiles
                  .filter(f => f)
                  .map((file, index) => (
                    <Box key={index} display="flex" justifyContent="space-between">
                      <Box display="flex" alignItems="center" mt={1}>
                        <DescriptionIcon
                          style={{
                            marginRight: 5,
                            color: hasInfoError(file.name) && 'darkgray',
                          }}
                        />
                        <Typography variant="body1" display="flex">
                          {hasInfoError(file.name) ? (
                            <s>{file.pieceType || file.name}</s>
                          ) : (
                            (file.pieceType || file.name) +
                            (file?.fileType?.description === 'Arquivos de requisitório'
                              ? ' (Arquivo de requisitório)'
                              : '')
                          )}
                          {filesInfo[file.name] && (
                            <span style={{ marginLeft: 5 }}>
                              <Tooltip title={filesInfo[file.name].message} arrow placement="right">
                                <InfoIcon
                                  style={{
                                    color: hasInfoError(file.name) ? 'darkred' : 'orange',
                                    fontSize: '15px',
                                  }}
                                />
                              </Tooltip>
                            </span>
                          )}
                        </Typography>
                      </Box>
                      <IconButton onClick={() => handleRemoveFile(file, index)}>
                        <HighlightOffIcon style={{ color: '#e81224' }} />
                      </IconButton>
                    </Box>
                  ))}
            </Box>
          </Grid>
          {showUrgent && (
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <Box display="flex" alignItems="center" marginBottom={4} marginTop={2}>
                <input
                  type="checkbox"
                  name="isUrgent"
                  checked={isUrgent}
                  onChange={handleChangeUrgent}
                />
                <Typography style={{ marginLeft: '8px' }} variant="h5">
                  Urgência Vara / Outras
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
        <Box display="flex" justifyContent="center" my={2}>
          <ButtonConsensus
            title="Enviar arquivos"
            onClick={onClose}
            disabled={loading || (!movementFiles?.length && pjeFileOptions?.length === 0)}
          />
        </Box>
        {!disableFiles && pjeFileOptions?.length > 0 ? (
          <>
            <Typography variant="h5">Arquivos a enviar para o PJE</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Enviar</TableCell>
                  <TableCell>Nome do arquivo</TableCell>
                  <TableCell>Status de processamento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pjeFileOptions.map((file, index) => {
                  const extensionNotAllowed = !PJE_FILE_MAX_SIZE_BY_TYPE[
                    file.name
                      .split('.')
                      .pop()
                      .toUpperCase()
                  ];
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Checkbox
                          value={file.id}
                          onChange={handleSelectFile}
                          id={String(index)}
                          checked={file.sendToPJE}
                          disabled={file.disabledReprocess || extensionNotAllowed}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        {extensionNotAllowed ? <s>{file.name}</s> : file?.name}
                        {extensionNotAllowed && (
                          <span style={{ marginLeft: 5 }}>
                            <Tooltip title="Tipo de arquivo não permitido." arrow placement="right">
                              <InfoIcon
                                style={{
                                  color: 'darkred',
                                  fontSize: '15px',
                                }}
                              />
                            </Tooltip>
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {parseFileProcessStatus(file.fileProcess?.processingStatus)}
                          {file.fileProcess?.processingStatus === 'ERROR' && (
                            <Tooltip title={parseFileProcessError(file.fileProcess)}>
                              <ErrorOutlineIcon
                                color="error"
                                style={{ marginLeft: 4, fontSize: 'small' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        ) : (
          <Typography>Não há arquivos no inquérito para o PJE</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalAddFile;
